/* eslint-disable no-unused-vars */
import React, {
  useCallback,
  useEffect,
  useState,
  useRef,
  CSSProperties,
} from "react";
// libraries
import classNames from "classnames";

// styles
import styles from "./styles.module.scss";
import { normalizeValue } from "../../../setup/util/helpers";

export enum InputType {
  DEFAULT = "DEFAULT",
  NUMBER = "NUMBER",
  PRICE = "PRICE",
  PHONE_NUMBER = "PHONE_NUMBER",
  DATE = "DATE",
  PASSWORD = "PASSWORD",
}

interface Props extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  defaultValue?: string | number;
  disabledValue?: string | number;
  maxCharacters?: number;
  maxDecimals?: number;
  maxValue?: number;
  // eslint-disable-next-line no-unused-vars
  handleChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  type?: InputType;
  required?: boolean;
  description?: string;
  hint?: string;
  errorMessage?: string;
  preventMaxValuePass?: boolean;
  // eslint-disable-next-line no-unused-vars
  onPriceChange?: (value: number | undefined) => void;
  containerWidth?: string;
  containerHeight?: string;
  outerContainerWidth?: string;
  placeholder?: string;
  isSettings?: boolean;
  disabled?: boolean;
  style?: CSSProperties;
  isError?: boolean;
}

const TextInput: React.FC<Props> = ({
  label = "",
  defaultValue = "",
  disabledValue = "",
  maxCharacters = 100,
  maxDecimals = 2,
  maxValue,
  handleChange,
  required,
  hint,
  type = InputType.DEFAULT,
  description = "",
  errorMessage,
  preventMaxValuePass = false,
  onPriceChange,
  containerWidth = "auto",
  containerHeight,
  outerContainerWidth,
  placeholder,
  isSettings,
  disabled,
  style,
  isError = false,
  onChange: onChangeProp,
  ...props
}: Props) => {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const prevPropsValueRef = useRef<
    string | number | readonly string[] | undefined
  >(props.value);
  const isFirstMountRef = useRef<boolean>(true);
  const prevDisabledValueRef = useRef<string | number | undefined>(
    disabledValue
  );

  const [value, setValue] = useState<string | number>(
    normalizeValue(props.value ?? defaultValue)
  );
  const [error, setError] = useState<boolean>(isError);
  const [focused, setFocused] = useState<boolean>(false);

  const onChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>): void => {
      if (
        preventMaxValuePass &&
        maxValue &&
        Number(e.target.value) > maxValue
      ) {
        return;
      }
      setError(isError);
      setValue(e.target.value);
      handleChange?.(e);
      onChangeProp?.(e);
      if (type === InputType.PRICE) {
        onPriceChange?.(
          e.target.value === "" ? undefined : Number(e.target.value)
        );
      }
    },
    [
      preventMaxValuePass,
      maxValue,
      isError,
      handleChange,
      onChangeProp,
      type,
      onPriceChange,
    ]
  );

  useEffect(() => {
    const wasDefined =
      prevDisabledValueRef.current != null &&
      prevDisabledValueRef.current !== "";
    const isNowUndefined = disabledValue == null || disabledValue === "";

    if (disabledValue != null && disabledValue !== "") {
      setValue(disabledValue || "");
    } else if (wasDefined && isNowUndefined) {
      setValue("");
    }

    prevDisabledValueRef.current = disabledValue;
  }, [disabledValue]);

  // Sync with external value changes (only when props.value changes externally, not during user input)
  useEffect(() => {
    const propsValueChanged = prevPropsValueRef.current !== props.value;
    const isFirstMount = isFirstMountRef.current;

    if (propsValueChanged || isFirstMount) {
      prevPropsValueRef.current = props.value;

      if (focused && !isFirstMount) {
        isFirstMountRef.current = false;
        return;
      }

      if (isSettings) {
        const normalizedDefault = normalizeValue(defaultValue);
        setValue(normalizedDefault || "");
      } else if (props.value != null && props.value !== "") {
        const normalized = normalizeValue(props.value);
        setValue(normalized);
      } else if (defaultValue != null && defaultValue !== "") {
        const normalizedDefault = normalizeValue(defaultValue);
        setValue(normalizedDefault);
      } else {
        setValue("");
      }
      isFirstMountRef.current = false;
    }
  }, [defaultValue, props.value, isSettings, focused]);

  useEffect(() => {
    const value = inputRef?.current?.value;

    if (focused && ((required && !value) || !!errorMessage)) {
      setError(true);
    } else {
      setError(isError);
    }
    if (maxValue && Number(value) > maxValue) {
      setError(true);
    }
  }, [
    focused,
    maxValue,
    required,
    value,
    inputRef?.current?.value,
    errorMessage,
    isError,
  ]);

  const validateNumber = (
    value: React.KeyboardEvent<HTMLTextAreaElement>["key"]
  ) => {
    if (/[0-9.]/.test(value)) return true;
    return false;
  };

  const errorClass = error ? styles.error : "";

  return (
    <div
      className={classNames(styles.container_outer)}
      style={{ width: outerContainerWidth ? outerContainerWidth : undefined }}>
      {label && (
        <div className={styles["header"]}>
          <p className={classNames(styles["p1"], styles["g400"])}>{label}</p>
        </div>
      )}
      <div
        className={classNames(styles.container, errorClass, {
          [styles.disabled]: disabled,
        })}
        style={{
          width: containerWidth,
          height: containerHeight ? containerHeight : undefined,
        }}>
        <input
          disabled={disabled}
          type={type === InputType.PASSWORD ? "password" : undefined}
          ref={inputRef}
          placeholder={placeholder}
          className={classNames(styles.input, {
            [styles["number"]]:
              type === InputType.NUMBER || type === InputType.PRICE,
          })}
          dir={
            type === InputType.NUMBER || type === InputType.PRICE
              ? "ltr"
              : undefined
          }
          onChange={onChange}
          style={{
            width: "100%",
            minHeight: "16px",
            ...style,
          }}
          onFocus={() => {
            setFocused(true);
          }}
          onBlur={() => {
            setFocused(false);
          }}
          onKeyPress={(event) => {
            const selectionStart = inputRef.current?.selectionStart;
            const selectionEnd = inputRef.current?.selectionEnd;
            const hasSelection =
              selectionStart !== null &&
              selectionEnd !== null &&
              selectionStart !== selectionEnd;

            // Use the live DOM value rather than React state — state can lag
            // one render behind during fast typing, causing stale closure bugs.
            const currentValue = inputRef.current?.value ?? "";

            if (!hasSelection && currentValue.length >= maxCharacters)
              event.preventDefault();

            if (type === InputType.NUMBER || type === InputType.PRICE) {
              if (!validateNumber(event?.key)) {
                event.preventDefault();
                return;
              }

              const effectiveMaxDecimals =
                type === InputType.PRICE ? 2 : maxDecimals;

              if (!hasSelection) {
                if (
                  currentValue.includes(".") &&
                  currentValue.split(".")[1].length >= effectiveMaxDecimals
                ) {
                  event.preventDefault();
                  return;
                }

                if (
                  (currentValue === "" && event.key === ".") ||
                  (currentValue.includes(".") && event.key === ".")
                ) {
                  event.preventDefault();
                  return;
                }
              }
            }

            if (
              type === InputType.PHONE_NUMBER &&
              (event?.key === " " ||
                event?.key === "e" ||
                (!validateNumber(event?.key) && event?.key !== "+") ||
                event?.key === "-" ||
                event?.key === ".")
            )
              event?.preventDefault();
          }}
          {...props}
          value={value}
        />
        {description && (
          <p className={classNames(styles["p4"], styles["g400"])}>
            {description}
          </p>
        )}
      </div>
      {/* Optional hint */}
      {hint && <div className={styles["hint"]}>{hint}</div>}
      <div className={styles["error_container"]}>
        {errorMessage && (
          <p dir={"rlt"} className={styles["error_text"]}>
            {errorMessage}
          </p>
        )}
      </div>
    </div>
  );
};

export default React.memo(TextInput);
